import { Button, Stack } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useCallback, useEffect } from "react";
import { useState } from "react";
import moment from "moment";
import { Box, styled } from "@mui/material";
import { TComparisonMode, useFromToContext } from "Providers/FromToProvider";
import { friendlyDate } from "Helpers/dates";
import { useUpdateEffect } from "react-use";
import AdvancedDatePicker from "features/analytics/components/AdvancedDatePicker";
import DatePickerSelector from "features/analytics/components/DatePickerSelector";

const StyledButton = styled(Button)(({ theme }) => ({
  height: 36,
  display: "flex",
  alignItems: "center",
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  padding: theme.spacing(2),
  minWidth: 10,
}));

interface IDatePickerDropdownProps {
  label: string;
  size?: "small" | "medium";
  readOnly?: boolean;
  hideAdvanced?: boolean;
}

function DatePickerDropdown({
  label,
  size = "medium",
  readOnly = false,
  hideAdvanced = false,
}: IDatePickerDropdownProps) {
  const {
    from,
    setFrom,
    to,
    setTo,
    lastNDays,
    setLastNDays,
    comparison,
    setComparison,
    comparisonMode,
    setComparisonMode,
    cFrom,
    setCFrom,
  } = useFromToContext();
  const [value, setValue] = useState("");
  const [advancedFrom, setAdvancedFrom] = useState<moment.Moment | null>(from);
  const [advancedTo, setAdvancedTo] = useState<moment.Moment | null>(to);
  const [advancedComparison, setAdvancedComparison] =
    useState<boolean>(comparison);
  const [advancedComparisonMode, setAdvancedComparisonMode] =
    useState<TComparisonMode>(comparisonMode);
  const [advancedCFrom, setAdvancedCFrom] = useState<moment.Moment | null>(
    cFrom,
  );
  const [advancedLastNDays, setAdvancedLastNDays] = useState<number | null>(
    lastNDays,
  );
  const [custom, setCustom] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const setDate = (value: string) => {
    setFrom(null);
    setTo(null);
    setAdvancedFrom(null);
    setAdvancedTo(null);
    setLastNDays(Number(value));
  };

  useEffect(() => {
    setCustom(null);
    if (lastNDays) {
      setValue(lastNDays.toString());
    } else {
      setValue("custom");
      setCustom(`${friendlyDate(from || "")} - ${friendlyDate(to || "")}`);
    }
  }, [from, to, lastNDays]);

  const onConfirm = useCallback(() => {
    setFrom(advancedFrom);
    setTo(advancedTo);
    setOpen(false);
    setComparison(advancedComparison);
    setComparisonMode(advancedComparisonMode);
    setCFrom(advancedCFrom);
    setLastNDays(advancedLastNDays);
  }, [
    advancedComparison,
    advancedFrom,
    advancedTo,
    advancedComparisonMode,
    advancedCFrom,
    advancedLastNDays,
    setComparison,
    setComparisonMode,
    setFrom,
    setLastNDays,
    setTo,
    setCFrom,
  ]);

  useUpdateEffect(() => {
    if (!open) {
      setAdvancedComparison(comparison);
      setAdvancedComparisonMode(comparisonMode);
      setAdvancedCFrom(cFrom);
      setAdvancedLastNDays(lastNDays);
      setAdvancedFrom(from);
      setAdvancedTo(to);
    }
  }, [comparison, comparisonMode, cFrom, lastNDays, from, to, open]);

  if (readOnly) {
    return (
      <Box display="flex" alignItems="center">
        {lastNDays
          ? `${friendlyDate(
              moment().subtract(lastNDays, "days").startOf("day"),
            )} - ${friendlyDate(moment().endOf("day"))}`
          : `${friendlyDate(from || "")} - ${friendlyDate(to || "")}`}
      </Box>
    );
  }

  return (
    <>
      <Stack direction="row" alignItems={"center"}>
        <DatePickerSelector
          value={value}
          setDate={setDate}
          custom={custom}
          label={label}
          size={size}
        />
        {!hideAdvanced && (
          <StyledButton onClick={() => setOpen(true)}>
            <CalendarMonthIcon sx={{ fontSize: 24 }} />
          </StyledButton>
        )}
      </Stack>

      <AdvancedDatePicker
        open={open}
        setOpen={setOpen}
        onSubmit={onConfirm}
        from={advancedFrom}
        setFrom={setAdvancedFrom}
        to={advancedTo}
        setTo={setAdvancedTo}
        comparison={advancedComparison}
        setComparison={setAdvancedComparison}
        comparisonMode={advancedComparisonMode}
        setComparisonMode={setAdvancedComparisonMode}
        cFrom={advancedCFrom}
        setCFrom={setAdvancedCFrom}
        lastNDays={advancedLastNDays}
        setLastNDays={setAdvancedLastNDays}
      />
    </>
  );
}

export default DatePickerDropdown;
